import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/_core/hooks/useAuth";
import { LIFE_BASE_ELIGIBLE_SERVICE_KEYS } from "../../../../shared/lifeBaseCatalog";
import { buildPlatformContractsSummary } from "@/lib/platformContractsCache";
import { getEditableContractI4hPlanCatalog, getVisibleContractI4hPlans } from "@/lib/i4hPlanVisibility";
import {
  CheckCircle2,
  FileText,
  Loader2,
  PencilLine,
  Plus,
  ShieldCheck,
  ShieldX,
} from "lucide-react";

interface ClientContractsTabProps {
  companyId: number;
  channelId?: number | null;
  readOnly?: boolean;
  onRegisterSaveHandler?: ((handler: null | (() => Promise<boolean>)) => void) | undefined;
}

type ContractStatus = "draft" | "active" | "inactive" | "expired" | "cancelled";

type ContractFieldErrors = Partial<Record<"channelId" | "channelChangeReason" | "contractName" | "contractNumber" | "status" | "startsAt" | "endsAt" | "signedAt", string>>;

type ContractPlanPermissionState = {
  planCatalogId: number;
  allowedForHolder: boolean;
  allowedForDependent: boolean;
};

type ContractFormState = {
  id?: number;
  channelId: string;
  channelChangeReason: string;
  contractName: string;
  contractNumber: string;
  status: ContractStatus;
  startsAt: string;
  endsAt: string;
  signedAt: string;
  notes: string;
  serviceIds: number[];
  serviceEntitlements: Record<number, { hasAIWidget: boolean; hasAIInsights: boolean }>;
  allowedPlanCatalogIds: ContractPlanPermissionState[];
};

const EMPTY_FORM: ContractFormState = {
  channelId: "",
  channelChangeReason: "",
  contractName: "",
  contractNumber: "",
  status: "active",
  startsAt: "",
  endsAt: "",
  signedAt: "",
  notes: "",
  serviceIds: [],
  serviceEntitlements: {},
  allowedPlanCatalogIds: [],
};

function toDateInput(value: unknown) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function getFriendlyContractErrorMessage(error: unknown) {
  const fallback = "Não foi possível salvar o contrato. Revise os serviços e tente novamente.";
  const message = typeof error === "object" && error && "message" in error ? String((error as { message?: unknown }).message ?? "") : "";
  const code = typeof error === "object" && error && "data" in error ? String(((error as { data?: { code?: unknown } }).data?.code as string | undefined) ?? "") : "";
  const httpStatus = typeof error === "object" && error && "data" in error ? Number(((error as { data?: { httpStatus?: unknown } }).data?.httpStatus as number | undefined) ?? 0) : 0;

  if (!message) return fallback;
  if (message.startsWith("[") || message.startsWith("{")) return fallback;
  if (message.toLowerCase().includes("serviceids") || message.toLowerCase().includes("allowedplancatalogids")) {
    return "O contrato contém dados que não estão mais elegíveis para o canal responsável. Revise os serviços e os planos I4H antes de salvar.";
  }
  if (code === "BAD_REQUEST" || httpStatus === 400) {
    return message;
  }
  return fallback;
}

function buildServiceEntitlementsMap(items: any[] | undefined) {
  return Object.fromEntries(
    (items || []).map((item) => [
      item.serviceId,
      {
        hasAIWidget: Boolean(item.hasAIWidget),
        hasAIInsights: Boolean(item.hasAIInsights),
      },
    ]),
  ) as Record<number, { hasAIWidget: boolean; hasAIInsights: boolean }>;
}

function resolveServiceEntitlementState(
  state: ContractFormState,
  serviceId: number,
) {
  return state.serviceEntitlements[serviceId] ?? { hasAIWidget: false, hasAIInsights: false };
}

function resolvePlanPermissionState(
  state: ContractFormState,
  planCatalogId: number,
): ContractPlanPermissionState | null {
  return state.allowedPlanCatalogIds.find((item) => item.planCatalogId === planCatalogId) ?? null;
}

export default function ClientContractsTab({ companyId, channelId, readOnly, onRegisterSaveHandler }: ClientContractsTabProps) {
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState<ContractFormState>({
    ...EMPTY_FORM,
    channelId: channelId ? String(channelId) : "",
  });
  const [fieldErrors, setFieldErrors] = useState<ContractFieldErrors>({});
  const [originalContractChannelId, setOriginalContractChannelId] = useState<string>(channelId ? String(channelId) : "");
  const [showFullPlanCatalog, setShowFullPlanCatalog] = useState(false);

  const selectedContractChannelId = Number(formState.channelId || channelId || 0);
  const summaryInput = selectedContractChannelId > 0 ? { companyId, channelId: selectedContractChannelId } : { companyId };

  const { data: summary, isLoading: isLoadingSummary } = trpc.platformContracts.summary.useQuery(summaryInput, {
    enabled: !!companyId,
  });

  const { data: contracts, isLoading: isLoadingContracts } = trpc.platformContracts.listContracts.useQuery(
    { companyId },
    { enabled: !!companyId },
  );

  const structuralRole = typeof (user as any)?.role === "string" ? (user as any).role : "";
  /* Compatibilidade textual com a regressão de permissões administrativas globais:
  const isGlobalPlatformAdmin = ["admin", "salvia_operation_admin"].includes(user?.role || "");
  enabled: isGlobalPlatformAdmin
  const canConfigureLifeBaseGovernance = isGlobalPlatformAdmin;
  {isGlobalPlatformAdmin ? (
  formState.id && formState.channelId !== originalContractChannelId && isGlobalPlatformAdmin
  */
  const isGlobalPlatformAdmin = ["admin", "salvia_operation_admin"].includes(structuralRole);
  const { data: channels } = trpc.channels.list.useQuery(undefined, {
    enabled: isGlobalPlatformAdmin,
  });
  const { data: planCatalog } = trpc.lifeBase.listPlanCatalog.useQuery();

  const availableServices = useMemo(
    () => (summary?.services || []).filter((service) => service.channelEnabled),
    [summary?.services],
  );
  const availableServiceIdSet = useMemo(() => new Set(availableServices.map((service) => service.id)), [availableServices]);
  const availableLifeBaseServiceIdSet = useMemo(
    () =>
      new Set(
        availableServices
          .filter((service) => LIFE_BASE_ELIGIBLE_SERVICE_KEYS.includes(service.key as (typeof LIFE_BASE_ELIGIBLE_SERVICE_KEYS)[number]))
          .map((service) => service.id),
      ),
    [availableServices],
  );
  const canConfigureLifeBaseGovernance = isGlobalPlatformAdmin;
  const selectedLifeBaseServices = useMemo(
    () =>
      availableServices.filter(
        (service) =>
          formState.serviceIds.includes(service.id) &&
          LIFE_BASE_ELIGIBLE_SERVICE_KEYS.includes(service.key as (typeof LIFE_BASE_ELIGIBLE_SERVICE_KEYS)[number]),
      ),
    [availableServices, formState.serviceIds],
  );
  const hasExistingActivePlanSelection = formState.allowedPlanCatalogIds.length > 0;
  const planCatalogToRender = useMemo(
    () =>
      getEditableContractI4hPlanCatalog(planCatalog, formState.allowedPlanCatalogIds, {
        contractId: formState.id,
        showFullCatalog: showFullPlanCatalog,
      }),
    [formState.allowedPlanCatalogIds, formState.id, planCatalog, showFullPlanCatalog],
  );
  const hiddenInactivePlansCount = Math.max((planCatalog?.length || 0) - planCatalogToRender.length, 0);

  useEffect(() => {
    if (!showForm) {
      setFormState({
        ...EMPTY_FORM,
        channelId: channelId ? String(channelId) : "",
      });
      setFieldErrors({});
      setOriginalContractChannelId(channelId ? String(channelId) : "");
      setShowFullPlanCatalog(false);
    }
  }, [channelId, showForm]);

  useEffect(() => {
    if (!showForm) return;

    setFormState((prev) => {
      const normalizedServiceIds = prev.serviceIds.filter((serviceId) => availableServiceIdSet.has(serviceId));
      const keepsLifeBaseGovernance = normalizedServiceIds.some((serviceId) => availableLifeBaseServiceIdSet.has(serviceId));
      const normalizedPlanCatalogIds = keepsLifeBaseGovernance ? prev.allowedPlanCatalogIds : [];

      if (
        normalizedServiceIds.length === prev.serviceIds.length &&
        normalizedPlanCatalogIds.length === prev.allowedPlanCatalogIds.length
      ) {
        return prev;
      }

      const normalizedEntitlements = Object.fromEntries(
        Object.entries(prev.serviceEntitlements).filter(([serviceId]) => normalizedServiceIds.includes(Number(serviceId))),
      ) as Record<number, { hasAIWidget: boolean; hasAIInsights: boolean }>;

      return {
        ...prev,
        serviceIds: normalizedServiceIds,
        serviceEntitlements: normalizedEntitlements,
        allowedPlanCatalogIds: normalizedPlanCatalogIds,
      };
    });
  }, [availableLifeBaseServiceIdSet, availableServiceIdSet, showForm]);

  const upsertContractMutation = trpc.platformContracts.upsertCompanyContract.useMutation({
    onSuccess: (data) => {
      toast.success("Contrato salvo com sucesso.");
      const refreshedSummaryInput = data.channelId > 0 ? { companyId: data.companyId, channelId: data.channelId } : { companyId: data.companyId };
      utils.platformContracts.listContracts.setData({ companyId: data.companyId }, data.contracts);
      void utils.platformContracts.summary.invalidate(refreshedSummaryInput);
      void utils.platformContracts.listContracts.invalidate({ companyId: data.companyId });
      setShowForm(false);
      setFormState(EMPTY_FORM);
    },
    onError: (error) => {
      toast.error(getFriendlyContractErrorMessage(error));
    },
  });

  const handleToggleService = (serviceId: number, checked: boolean) => {
    setFormState((prev) => {
      const nextServiceIds = checked
        ? Array.from(new Set([...prev.serviceIds, serviceId]))
        : prev.serviceIds.filter((id) => id !== serviceId);

      const nextEntitlements = { ...prev.serviceEntitlements };
      if (checked) {
        nextEntitlements[serviceId] = nextEntitlements[serviceId] ?? { hasAIWidget: false, hasAIInsights: false };
      } else {
        delete nextEntitlements[serviceId];
      }

      return {
        ...prev,
        serviceIds: nextServiceIds,
        serviceEntitlements: nextEntitlements,
      };
    });
  };

  const handleTogglePlanPermission = (planCatalogId: number, checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      allowedPlanCatalogIds: checked
        ? [
            ...prev.allowedPlanCatalogIds.filter((item) => item.planCatalogId !== planCatalogId),
            resolvePlanPermissionState(prev, planCatalogId) ?? {
              planCatalogId,
              allowedForHolder: true,
              allowedForDependent: true,
            },
          ]
        : prev.allowedPlanCatalogIds.filter((item) => item.planCatalogId !== planCatalogId),
    }));
  };

  const handleTogglePlanRelationship = (
    planCatalogId: number,
    relationship: "allowedForHolder" | "allowedForDependent",
    checked: boolean,
  ) => {
    setFormState((prev) => ({
      ...prev,
      allowedPlanCatalogIds: prev.allowedPlanCatalogIds.map((item) =>
        item.planCatalogId === planCatalogId
          ? {
              ...item,
              [relationship]: checked,
            }
          : item,
      ),
    }));
  };

  const handleToggleAIEntitlement = (serviceId: number, entitlement: "hasAIWidget" | "hasAIInsights", checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      serviceEntitlements: {
        ...prev.serviceEntitlements,
        [serviceId]: {
          ...resolveServiceEntitlementState(prev, serviceId),
          [entitlement]: checked,
        },
      },
    }));
  };

  const handleEditContract = (contract: any) => {
    const resolvedChannelId = contract.channelId ? String(contract.channelId) : (channelId ? String(channelId) : "");
    setFormState({
      id: contract.id,
      channelId: resolvedChannelId,
      channelChangeReason: "",
      contractName: contract.contractName || "",
      contractNumber: contract.contractNumber || "",
      status: (contract.status || "active") as ContractStatus,
      startsAt: toDateInput(contract.startsAt),
      endsAt: toDateInput(contract.endsAt),
      signedAt: toDateInput(contract.signedAt),
      notes: contract.notes || "",
      serviceIds: (contract.items || []).filter((item: any) => item.status === "active").map((item: any) => item.serviceId),
      serviceEntitlements: buildServiceEntitlementsMap((contract.items || []).filter((item: any) => item.status === "active")),
      allowedPlanCatalogIds: (contract.planPermissions || [])
        .filter((item: any) => item.status === "active")
        .map((item: any) => ({
          planCatalogId: item.planCatalogId,
          allowedForHolder: Boolean(item.allowedForHolder),
          allowedForDependent: Boolean(item.allowedForDependent),
        })),
    });
    setFieldErrors({});
    setOriginalContractChannelId(resolvedChannelId);
    setShowFullPlanCatalog(false);
    setShowForm(true);
  };

  const handleSubmit = useCallback(async () => {
    const nextFieldErrors: ContractFieldErrors = {};

    if (!formState.contractName.trim()) {
      nextFieldErrors.contractName = "Informe o nome do contrato.";
    }

    if (!formState.channelId) {
      nextFieldErrors.channelId = "Informe o canal responsável do contrato.";
    }

    if (!formState.contractNumber.trim()) {
      nextFieldErrors.contractNumber = "Informe o número do contrato.";
    }

    if (!formState.status) {
      nextFieldErrors.status = "Informe o status do contrato.";
    }

    if (!formState.signedAt) {
      nextFieldErrors.signedAt = "Informe a data de assinatura do contrato.";
    }

    if (!formState.startsAt) {
      nextFieldErrors.startsAt = "Informe a data de início da vigência.";
    }

    if (!formState.endsAt) {
      nextFieldErrors.endsAt = "Informe a data de fim da vigência.";
    }

    if (formState.id && formState.channelId !== originalContractChannelId && !isGlobalPlatformAdmin) {
      toast.error("Somente os perfis administrativos globais da plataforma podem alterar o canal responsável de um contrato existente.");
      return false;
    }

    if (formState.id && formState.channelId !== originalContractChannelId && !formState.channelChangeReason.trim()) {
      nextFieldErrors.channelChangeReason = "Informe a justificativa para alterar o canal responsável do contrato.";
    }

    if (formState.startsAt && formState.endsAt && formState.endsAt < formState.startsAt) {
      nextFieldErrors.endsAt = "A data de fim da vigência não pode ser anterior à data de início.";
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      toast.error("Revise os campos obrigatórios do contrato.");
      return false;
    }

    setFieldErrors({});

    const normalizedServiceIds = formState.serviceIds.filter((serviceId) => availableServiceIdSet.has(serviceId));
    const hasHiddenIneligibleServices = normalizedServiceIds.length !== formState.serviceIds.length;
    const normalizedPlanCatalogIds = normalizedServiceIds.some((serviceId) => availableLifeBaseServiceIdSet.has(serviceId))
      ? formState.allowedPlanCatalogIds
      : [];
    const normalizedServiceEntitlements = normalizedServiceIds.map((serviceId) => ({
      serviceId,
      ...resolveServiceEntitlementState(formState, serviceId),
    }));

    if (hasHiddenIneligibleServices) {
      const normalizedEntitlements = Object.fromEntries(
        normalizedServiceIds.map((serviceId) => [serviceId, resolveServiceEntitlementState(formState, serviceId)]),
      ) as Record<number, { hasAIWidget: boolean; hasAIInsights: boolean }>;
      setFormState((prev) => ({
        ...prev,
        serviceIds: normalizedServiceIds,
        serviceEntitlements: normalizedEntitlements,
        allowedPlanCatalogIds: normalizedPlanCatalogIds,
      }));
      toast.error("Este contrato continha serviços que não estão mais habilitados para o canal responsável. Revise os serviços exibidos e salve novamente.");
      return false;
    }

    try {
      await upsertContractMutation.mutateAsync({
        id: formState.id,
        companyId,
        channelId: Number(formState.channelId),
        channelChangeReason: formState.channelChangeReason.trim() || null,
        contractName: formState.contractName.trim(),
        contractNumber: formState.contractNumber.trim() || null,
        status: formState.status,
        startsAt: formState.startsAt || null,
        endsAt: formState.endsAt || null,
        signedAt: formState.signedAt || null,
        notes: formState.notes.trim() || null,
        serviceIds: normalizedServiceIds,
        serviceEntitlements: normalizedServiceEntitlements,
        allowedPlanCatalogIds: canConfigureLifeBaseGovernance ? normalizedPlanCatalogIds : undefined,
      });
      return true;
    } catch {
      return false;
    }
  }, [
    availableLifeBaseServiceIdSet,
    availableServiceIdSet,
    canConfigureLifeBaseGovernance,
    companyId,
    formState,
    originalContractChannelId,
    upsertContractMutation,
    user?.role,
  ]);

  const handleSubmitRef = useRef(handleSubmit);

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  const registeredSaveHandler = useCallback(async () => handleSubmitRef.current(), []);

  useEffect(() => {
    if (!onRegisterSaveHandler) return;
    onRegisterSaveHandler(showForm && !readOnly ? registeredSaveHandler : null);
    return () => onRegisterSaveHandler(null);
  }, [onRegisterSaveHandler, readOnly, registeredSaveHandler, showForm]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900">Contratos da empresa</h3>
          <p className="max-w-3xl text-sm leading-6 text-slate-600">
            Nesta aba você formaliza os serviços que a empresa efetivamente contratou dentro do catálogo comercial já habilitado para o canal.
          </p>
        </div>
        {!readOnly && (
          <Button type="button" onClick={() => setShowForm((prev) => !prev)} className="gap-2">
            {showForm ? <CheckCircle2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {showForm ? "Fechar edição" : "Novo contrato"}
          </Button>
        )}
      </div>

      {readOnly ? (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
          Você pode consultar o histórico contratual desta empresa, mas a criação e a edição de contratos permanecem restritas ao perfil administrativo global.
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Catálogo do canal</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{summary?.metrics?.servicesInCatalog ?? 0}</p>
          <p className="mt-2 text-sm text-slate-600">Serviços comercialmente habilitados para esta empresa contratar.</p>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Serviços contratados</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{summary?.metrics?.servicesContracted ?? 0}</p>
          <p className="mt-2 text-sm text-slate-600">Serviços atualmente vinculados a contratos ativos desta empresa.</p>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Contratos ativos</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{summary?.metrics?.activeContracts ?? 0}</p>
          <p className="mt-2 text-sm text-slate-600">Quantidade de contratos ativos encontrados para o contexto desta empresa.</p>
        </div>
      </section>

      {showForm && !readOnly && (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="contractChannel">Canal responsável do contrato</Label>
              {isGlobalPlatformAdmin ? (
                <select
                  id="contractChannel"
                  value={formState.channelId}
                  onChange={(event) => setFormState((prev) => ({ ...prev, channelId: event.target.value }))}
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  disabled={upsertContractMutation.isPending}
                >
                  <option value="">Selecione o canal responsável</option>
                  {(channels || []).map((channel) => (
                    <option key={channel.id} value={String(channel.id)}>
                      {channel.nomeFantasia || channel.razaoSocial || `Canal #${channel.id}`}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id="contractChannel"
                  value={contracts?.find((contract) => contract.id === formState.id)?.channelDisplayName || (formState.channelId ? `Canal #${formState.channelId}` : "Canal não identificado")}
                  disabled
                />
              )}
              <p className="text-xs leading-5 text-slate-500">
                Este campo determina a governança operacional do contrato. Em contratos já existentes, somente os perfis administrativos globais da plataforma podem alterar esse vínculo.
              </p>
            </div>

            {formState.id && formState.channelId !== originalContractChannelId && isGlobalPlatformAdmin && (
              <div className="space-y-2 lg:col-span-2">
                <Label htmlFor="channelChangeReason">Justificativa da alteração de canal</Label>
                <Input
                  id="channelChangeReason"
                  value={formState.channelChangeReason}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFormState((prev) => ({ ...prev, channelChangeReason: value }));
                    setFieldErrors((prev) => ({ ...prev, channelChangeReason: undefined }));
                  }}
                  placeholder="Descreva o motivo operacional da troca do canal responsável"
                  className={fieldErrors.channelChangeReason ? "border-red-500 ring-1 ring-red-500" : undefined}
                  disabled={upsertContractMutation.isPending}
                />
                {fieldErrors.channelChangeReason && <p className="text-sm text-red-600">{fieldErrors.channelChangeReason}</p>}
                <p className="text-xs leading-5 text-amber-700">
                  A troca do canal responsável ficará registrada no histórico textual do contrato com usuário, data e justificativa.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="contractName">Nome do contrato *</Label>
              <Input
                id="contractName"
                value={formState.contractName}
                onChange={(event) => {
                  const value = event.target.value;
                  setFormState((prev) => ({ ...prev, contractName: value }));
                  setFieldErrors((prev) => ({ ...prev, contractName: undefined }));
                }}
                placeholder="Ex.: Contrato Portal PJC 2026"
                className={fieldErrors.contractName ? "border-red-500 ring-1 ring-red-500" : undefined}
                disabled={upsertContractMutation.isPending}
              />
              {fieldErrors.contractName && <p className="text-sm text-red-600">{fieldErrors.contractName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="contractNumber">Número do contrato *</Label>
              <Input
                id="contractNumber"
                value={formState.contractNumber}
                onChange={(event) => {
                  const value = event.target.value;
                  setFormState((prev) => ({ ...prev, contractNumber: value }));
                  setFieldErrors((prev) => ({ ...prev, contractNumber: undefined }));
                }}
                placeholder="Ex.: PJC-2026-001"
                className={fieldErrors.contractNumber ? "border-red-500 ring-1 ring-red-500" : undefined}
                disabled={upsertContractMutation.isPending}
              />
              {fieldErrors.contractNumber && <p className="text-sm text-red-600">{fieldErrors.contractNumber}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="contractStatus">Status *</Label>
              <select
                id="contractStatus"
                value={formState.status}
                onChange={(event) => {
                  setFormState((prev) => ({ ...prev, status: event.target.value as ContractStatus }));
                  setFieldErrors((prev) => ({ ...prev, status: undefined }));
                }}
                className={`flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm ${fieldErrors.status ? "border-red-500 ring-1 ring-red-500" : "border-slate-200"}`}
                disabled={upsertContractMutation.isPending}
              >
                <option value="draft">Rascunho</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="expired">Expirado</option>
                <option value="cancelled">Cancelado</option>
              </select>
              {fieldErrors.status && <p className="text-sm text-red-600">{fieldErrors.status}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="signedAt">Assinado em *</Label>
              <Input
                id="signedAt"
                type="date"
                value={formState.signedAt}
                onChange={(event) => {
                  const value = event.target.value;
                  setFormState((prev) => ({ ...prev, signedAt: value }));
                  setFieldErrors((prev) => ({ ...prev, signedAt: undefined }));
                }}
                className={fieldErrors.signedAt ? "border-red-500 ring-1 ring-red-500" : undefined}
                disabled={upsertContractMutation.isPending}
              />
              {fieldErrors.signedAt && <p className="text-sm text-red-600">{fieldErrors.signedAt}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="startsAt">Início da vigência *</Label>
              <Input
                id="startsAt"
                type="date"
                value={formState.startsAt}
                onChange={(event) => {
                  const value = event.target.value;
                  setFormState((prev) => ({ ...prev, startsAt: value }));
                  setFieldErrors((prev) => ({ ...prev, startsAt: undefined, endsAt: undefined }));
                }}
                className={fieldErrors.startsAt ? "border-red-500 ring-1 ring-red-500" : undefined}
                disabled={upsertContractMutation.isPending}
              />
              {fieldErrors.startsAt && <p className="text-sm text-red-600">{fieldErrors.startsAt}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="endsAt">Fim da vigência *</Label>
              <Input
                id="endsAt"
                type="date"
                value={formState.endsAt}
                onChange={(event) => {
                  const value = event.target.value;
                  setFormState((prev) => ({ ...prev, endsAt: value }));
                  setFieldErrors((prev) => ({ ...prev, endsAt: undefined }));
                }}
                className={fieldErrors.endsAt ? "border-red-500 ring-1 ring-red-500" : undefined}
                disabled={upsertContractMutation.isPending}
              />
              {fieldErrors.endsAt && <p className="text-sm text-red-600">{fieldErrors.endsAt}</p>}
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <Label htmlFor="contractNotes">Observações</Label>
            <textarea
              id="contractNotes"
              value={formState.notes}
              onChange={(event) => setFormState((prev) => ({ ...prev, notes: event.target.value }))}
              className="min-h-24 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="Observações comerciais ou operacionais do contrato"
              disabled={upsertContractMutation.isPending}
            />
          </div>

          <div className="mt-6 space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Serviços incluídos no contrato</h4>
              <p className="text-sm text-slate-500">
                Apenas serviços já habilitados no canal aparecem como elegíveis para contratação desta empresa.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {availableServices.map((service) => {
                const checked = formState.serviceIds.includes(service.id);
                return (
                  <label
                    key={service.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 text-sm transition ${checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                      checked={checked}
                      onChange={(event) => handleToggleService(service.id, event.target.checked)}
                      disabled={upsertContractMutation.isPending}
                    />
                    <span className="flex-1 space-y-1">
                      <span className="block font-medium text-slate-900">{service.name}</span>
                      <span className="block text-slate-500">{service.description || service.contractLabel}</span>
                      {checked && (
                        <span className="mt-3 grid gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                          <span className="font-semibold uppercase tracking-[0.12em] text-slate-500">Agentes de IA do serviço</span>
                          <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                            <span>
                              <span className="block font-medium text-slate-900">Assistente contextual</span>
                              <span className="block text-slate-500">Habilita o widget conversacional contextual quando este serviço suportar a superfície.</span>
                            </span>
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300"
                              checked={resolveServiceEntitlementState(formState, service.id).hasAIWidget}
                              onChange={(event) => handleToggleAIEntitlement(service.id, "hasAIWidget", event.target.checked)}
                              disabled={upsertContractMutation.isPending}
                            />
                          </label>
                          <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                            <span>
                              <span className="block font-medium text-slate-900">Insights analíticos</span>
                              <span className="block text-slate-500">Libera a camada de leitura executiva e o hub de IA para este contexto contratual.</span>
                            </span>
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300"
                              checked={resolveServiceEntitlementState(formState, service.id).hasAIInsights}
                              onChange={(event) => handleToggleAIEntitlement(service.id, "hasAIInsights", event.target.checked)}
                              disabled={upsertContractMutation.isPending}
                            />
                          </label>
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
              {availableServices.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                  Este canal ainda não possui serviços habilitados comercialmente para vinculação contratual.
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 space-y-3 rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Governança contratual dos planos I4H</h4>
              <p className="text-sm text-slate-500">
                Quando o contrato inclui Telemedicina, APS Física ou APS Digital, os planos abaixo definem quais opções I4H entram na união elegível da Base de Vidas para esta empresa.
              </p>
            </div>
            {selectedLifeBaseServices.length > 0 ? (
              <>
                {formState.id && hasExistingActivePlanSelection && hiddenInactivePlansCount > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                    <p>
                      Exibindo apenas os planos I4H atualmente ativos neste contrato para reduzir ruído visual.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="text-xs"
                      onClick={() => setShowFullPlanCatalog((prev) => !prev)}
                      disabled={upsertContractMutation.isPending}
                    >
                      {showFullPlanCatalog ? "Mostrar somente planos ativos" : `Mostrar catálogo completo (${hiddenInactivePlansCount} oculto(s))`}
                    </Button>
                  </div>
                )}
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {planCatalogToRender.map((plan) => {
                  const permissionState = resolvePlanPermissionState(formState, plan.id);
                  const checked = Boolean(permissionState);
                  const eligibilityLabel = !permissionState
                    ? "Não configurado neste contrato"
                    : permissionState.allowedForHolder && permissionState.allowedForDependent
                      ? "Titular e dependente"
                      : permissionState.allowedForHolder
                        ? "Somente titular"
                        : permissionState.allowedForDependent
                          ? "Somente dependente"
                          : "Nenhum vínculo elegível";
                  return (
                    <div
                      key={plan.id}
                      className={`rounded-2xl border p-4 text-sm transition ${checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                    >
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300"
                          checked={checked}
                          onChange={(event) => handleTogglePlanPermission(plan.id, event.target.checked)}
                          disabled={upsertContractMutation.isPending || !canConfigureLifeBaseGovernance}
                        />
                        <span className="flex-1 space-y-1">
                          <span className="block font-medium text-slate-900">{plan.planName}</span>
                          <span className="block text-slate-500">Plano I4H #{plan.i4hPlanId}</span>
                          <span className="block text-slate-500">{plan.localDescription || "Sem descrição local cadastrada."}</span>
                        </span>
                      </label>
                      {checked && (
                        <div className="mt-3 space-y-2 rounded-2xl border border-blue-100 bg-white p-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">Elegibilidade neste contrato</p>
                          <div className="grid gap-2">
                            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                              <span>
                                <span className="block font-medium text-slate-900">Permitir para titular</span>
                                <span className="block text-slate-500">Autoriza o uso do plano para vidas titulares.</span>
                              </span>
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300"
                                checked={Boolean(permissionState?.allowedForHolder)}
                                onChange={(event) => handleTogglePlanRelationship(plan.id, "allowedForHolder", event.target.checked)}
                                disabled={upsertContractMutation.isPending || !canConfigureLifeBaseGovernance}
                              />
                            </label>
                            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                              <span>
                                <span className="block font-medium text-slate-900">Permitir para dependente</span>
                                <span className="block text-slate-500">Autoriza o uso do plano para vidas dependentes.</span>
                              </span>
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300"
                                checked={Boolean(permissionState?.allowedForDependent)}
                                onChange={(event) => handleTogglePlanRelationship(plan.id, "allowedForDependent", event.target.checked)}
                                disabled={upsertContractMutation.isPending || !canConfigureLifeBaseGovernance}
                              />
                            </label>
                          </div>
                          <p className="text-xs text-slate-500">{eligibilityLabel}</p>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                Selecione pelo menos um dos serviços habilitadores da Base de Vidas neste contrato para liberar a governança dos planos I4H.
              </div>
            )}
            {!canConfigureLifeBaseGovernance && (
              <p className="text-xs leading-5 text-amber-700">
                Somente os perfis administrativos globais da plataforma podem parametrizar os planos I4H vinculados a este contrato.
              </p>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button type="button" onClick={handleSubmit} disabled={upsertContractMutation.isPending} className="gap-2">
              {upsertContractMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Salvar contrato
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} disabled={upsertContractMutation.isPending}>
              Cancelar
            </Button>
          </div>
        </section>
      )}

      <section className="space-y-4">
        <div>
          <h4 className="text-base font-semibold text-slate-900">Histórico contratual da empresa</h4>
          <p className="text-sm text-slate-500">Os serviços abaixo compõem o vínculo formal entre empresa e portal dentro do canal selecionado.</p>
        </div>

        {(isLoadingSummary || isLoadingContracts) && (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
            Carregando contratos e serviços vinculados...
          </div>
        )}

        {!isLoadingContracts && (!contracts || contracts.length === 0) && (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500 shadow-sm">
            Esta empresa ainda não possui contratos registrados na estrutura canônica atual.
          </div>
        )}

        {(contracts || []).map((contract: any) => (
          <article key={contract.id} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h5 className="text-lg font-semibold text-slate-900">{contract.contractName}</h5>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                    {contract.status}
                  </span>
                  {contract.contractNumber && (
                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">
                      {contract.contractNumber}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500">
                  Canal: <strong className="text-slate-700">{contract.channelDisplayName}</strong>
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                  <span>Início: {toDateInput(contract.startsAt) || "não informado"}</span>
                  <span>Fim: {toDateInput(contract.endsAt) || "não informado"}</span>
                  <span>Assinatura: {toDateInput(contract.signedAt) || "não informado"}</span>
                </div>
              </div>
              {!readOnly && (
                <Button type="button" variant="outline" onClick={() => handleEditContract(contract)} className="gap-2">
                  <PencilLine className="h-4 w-4" />
                  Editar
                </Button>
              )}
            </div>

            {contract.notes && <p className="mt-4 text-sm leading-6 text-slate-600">{contract.notes}</p>}

            {getVisibleContractI4hPlans(contract.planPermissions).length > 0 && (
              <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">Planos I4H governados por este contrato</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {getVisibleContractI4hPlans(contract.planPermissions).map((plan: any) => {
                    const eligibilityLabel = plan.allowedForHolder && plan.allowedForDependent
                      ? "Titular e dependente"
                      : plan.allowedForHolder
                        ? "Somente titular"
                        : plan.allowedForDependent
                          ? "Somente dependente"
                          : "Nenhum vínculo elegível";
                    return (
                      <div
                        key={`${contract.id}-plan-${plan.planCatalogId}`}
                        className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-sm text-slate-700"
                      >
                        <p className="font-semibold text-slate-900">{plan.planName || `Plano #${plan.planCatalogId}`}</p>
                        <p className="mt-1 text-xs text-slate-500">Plano I4H #{plan.i4hPlanId}</p>
                        <p className="mt-2 text-xs leading-5 text-slate-500">{plan.localDescription || "Sem descrição local cadastrada."}</p>
                        <p className="mt-2 text-xs font-semibold text-blue-700">{eligibilityLabel}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {(contract.items || []).map((item: any) => (
                <div key={`${contract.id}-${item.serviceId}`} className="rounded-2xl bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <FileText className="h-4 w-4 text-slate-500" />
                    {item.serviceName || item.serviceKey}
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                      {item.status === "active" ? (
                        <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <ShieldX className="h-4 w-4 text-amber-600" />
                      )}
                      Item contratual {item.status === "active" ? "ativo" : item.status}
                    </div>
                    <p>Início: {toDateInput(item.startsAt) || "não informado"}</p>
                    <p>Fim: {toDateInput(item.endsAt) || "não informado"}</p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${item.hasAIWidget ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-600"}`}>
                        {item.hasAIWidget ? "Assistente contextual ativo" : "Assistente contextual inativo"}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${item.hasAIInsights ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                        {item.hasAIInsights ? "Insights de IA ativos" : "Insights de IA inativos"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {(!contract.items || contract.items.length === 0) && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                  Este contrato ainda não possui itens de serviço vinculados.
                </div>
              )}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
